// SPDX-License-Identifier: GPL-3.0-or-later
// Turns the typed engine events into feedback events: ball start, score, ball save, mode start,
// mode complete and game over. The engine already reports these, so the bridge only listens. It
// uses `engine.on`, which is a read-only view of the game. It never calls the engine.
//
// The engine delivers its events after each `step()`. Contact events from the rules come during
// the step. Both reach the sink in the same frame.

import type { EngineEvent, PinballEngine } from "../../shared/contracts.ts";
import type { FeedbackEvent, FeedbackSink } from "../../shared/feedback/index.ts";
import { modeLabel } from "../modes.ts";
import type { PositionOf } from "./positions.ts";

const NO_POSITION: PositionOf = () => undefined;

/** The feedback event of an engine event, or null when the engine event has none. */
export function feedbackFromEngine(
  ev: EngineEvent,
  positionOf: PositionOf = NO_POSITION,
): FeedbackEvent | null {
  switch (ev.type) {
    case "ball_start":
      return { kind: "ball_start", ballNumber: ev.ballNumber };
    case "score":
      return {
        kind: "score",
        scoreDelta: ev.points,
        label: ev.reason,
        element: ev.item,
        pos: ev.item === undefined ? undefined : positionOf(ev.item),
      };
    case "ball_saved":
      return { kind: "ball_save", strength: 1, label: "Ball saved" };
    case "mode_start":
      return { kind: "mode_start", modeId: ev.modeId, label: ev.label, strength: 1 };
    case "mode_end":
      return ev.completed
        ? {
            kind: "mode_complete",
            modeId: ev.modeId,
            label: modeLabel(ev.modeId),
            scoreDelta: ev.bonus,
            strength: 1,
          }
        : null;
    case "game_over":
      return { kind: "game_over", finalScore: ev.finalScore, strength: 1 };
    default:
      return null;
  }
}

/** Listen to an engine and emit its feedback events. The result stops the listening. */
export function bridgeEngineEvents(
  engine: Pick<PinballEngine, "on">,
  sink: FeedbackSink,
  positionOf: PositionOf = NO_POSITION,
): () => void {
  return engine.on((ev) => {
    const out = feedbackFromEngine(ev, positionOf);
    if (out !== null) sink.emit(out);
  });
}
